import { z } from "zod";

import { AddressSchema } from "../address";

export const SubscriptionSchema = z.object({
    id: z.string(),
    productId: z.string().nullable().optional(),
    priceId: z.string().nullable().optional(),
    orderId: z.string().nullable().optional(),
    currency: z.string().default('USD'),
    quantity: z.number().default(1),
    customerId: z.string().nullable().optional(),
    defaultPaymentInstrumentId: z.string().nullable().optional(),
    preferredProcessorId: z.string().nullable().optional(),
    billingCycleAnchor: z.date().nullable().optional(),
    status: z.string().default('active'),
    cancelAt: z.date().nullable().optional(),
    cancelAtPeriodEnd: z.boolean().default(false),
    canceledAt: z.date().nullable().optional(),
    endedAt: z.date().nullable().optional(),
    currentPeriodEnd: z.date().nullable().optional(),
    currentPeriodStart: z.date().nullable().optional(),
    nextBillingDate: z.date().nullable().optional(),
    trialStart: z.date().nullable().optional(),
    trialEnd: z.date().nullable().optional(),
    draft: z.boolean().default(false),
    createdAt: z.date().default(() => new Date()),
    updatedAt: z.date().default(() => new Date()),
    // Joined fields from listSubscriptions service
    storeId: z.string().nullable().optional(),
    storeName: z.string().nullable().optional(),
    customerEmail: z.string().nullable().optional(),
    customer: z.string().nullable().optional(),
    priceCurrencyOptions: z.any().nullable().optional(),
    priceInterval: z.string().nullable().optional(),
    priceIntervalCount: z.number().nullable().optional(),
    priceRecurring: z.boolean().nullable().optional(),
    priceDefault: z.boolean().nullable().optional(),
    sortableAmount: z.coerce.number().nullable().optional(),
    priceSettings: z.any().nullable().optional(),
    processorName: z.string().nullable().optional(),
    processorId: z.string().nullable().optional(),
    paymentId: z.string().nullable().optional(),
    useTagadaVault: z.boolean().nullable().optional(),
    paymentType: z.string().nullable(),
    shippingRateId: z.string().nullable().optional(),
    subscriptionGroupId: z.string().nullable().optional(),
});

const CustomerSchema = z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    billingAddress: AddressSchema.nullable().optional(),
    shippingAddress: AddressSchema.nullable().optional(),
});

const OrderSchema = z.object({
    id: z.string(),
    status: z.string(),
    items: z.array(z.object({
        id: z.string(),
        quantity: z.number().default(1),
        price: z.number().default(0),
        product: z.object({
            id: z.string(),
            name: z.string(),
        }).nullable().optional(),
    })),
});

const ProductSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable().optional(),
});


const ProcessorSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: z.string().nullable().optional(),
});

const CurrencyOptionSnapshotSchema = z.object({
    // JSONB columns preserve the writer's typeof; historical rows may hold string amounts/rates.
    rate: z.coerce.number().optional(),
    amount: z.coerce.number(),
    lock: z.boolean().optional(),
    date: z.string().optional(),
});

const PriceSnapshotSchema = z.object({
    id: z.string(),
    subscriptionId: z.string(),
    accountId: z.string().nullable().optional(),
    default: z.boolean().nullable().optional(),
    currencyOptions: z.record(z.string(), CurrencyOptionSnapshotSchema),
    recurring: z.boolean().nullable().optional(),
    billingTiming: z.string().nullable().optional(),
    interval: z.string().nullable().optional(),
    intervalCount: z.number().nullable().optional(),
    rebillWithOrder: z.boolean().nullable().optional(),
    rebillMode: z.enum(['capture', 'auth']).nullable().optional(),
    rebillStepIntervalMs: z.number().nullable().optional(),
    isExternalSellingPlan: z.boolean().nullable().optional(),
    isInternalSellingPlan: z.boolean().nullable().optional(),
    shopifySellingPlanId: z.string().nullable().optional(),
    creditsPerRebill: z.number().nullable().optional(),
    deliverySettings: z.any().nullable().optional(),
    priceSettings: z.any().nullable().optional(),
    createdAt: z.date().nullable().optional(),
    updatedAt: z.date().nullable().optional(),
});

const PaymentSchema = z.object({
    id: z.string(),
    amount: z.number().default(0),
    currency: z.string().default('USD'),
    status: z.string().default('pending'),
    payment: z.object({
        createdAt: z.date().default(() => new Date()),
        disputeStatus: z.string().nullable(),
        disputeType: z.string().nullable(),
        disputeReasonCode: z.string().nullable(),
        disputedTransactionId: z.string().nullable(),
        disputedAt: z.date().nullable(),
        disputeNote: z.string().nullable(),
        isDisputed: z.boolean().default(false),
        id: z.string(),
        status: z.string().default('pending'),
        amount: z.number().default(0),
        retryIndex: z.number().nullable().optional(),
        chainIndex: z.number().nullable().optional(),
        customer: CustomerSchema.nullable().optional(),
        paymentInstrument: z.object({
            id: z.string(),
            type: z.string(),
            card: z.object({
                brand: z.string().nullable().optional(),
                last4: z.string().nullable().optional(),
            }).nullable().optional(),
        }).nullable().optional(),
    }).nullable().optional(),
});

export const SubscriptionDetailsSchema = z.object({
    id: z.string(),
    productId: z.string().nullable().optional(),
    priceId: z.string().nullable().optional(),
    orderId: z.string().nullable().optional(),
    currency: z.string().default('USD'),
    quantity: z.number().default(1),
    customerId: z.string().nullable().optional(),
    defaultPaymentInstrumentId: z.string().nullable().optional(),
    preferredProcessorId: z.string().nullable().optional(),
    billingCycleAnchor: z.date().nullable().optional(),
    status: z.string().default('active'),
    cancelAt: z.date().nullable().optional(),
    cancelAtPeriodEnd: z.boolean().default(false),
    canceledAt: z.date().nullable().optional(),
    endedAt: z.date().nullable().optional(),
    currentPeriodEnd: z.date().nullable().optional(),
    currentPeriodStart: z.date().nullable().optional(),
    nextBillingDate: z.date().nullable().optional(),
    trialStart: z.date().nullable().optional(),
    trialEnd: z.date().nullable().optional(),
    draft: z.boolean().default(false),
    storeId: z.string().nullable().optional(),
    shippingRateId: z.string().nullable().optional(),
    subscriptionGroupId: z.string().nullable().optional(),
    createdAt: z.date().default(() => new Date()),
    updatedAt: z.date().default(() => new Date()),
    // Relations
    customer: CustomerSchema.nullable().optional(),
    order: OrderSchema.nullable().optional(),
    product: ProductSchema.nullable().optional(),
    priceSnapshot: PriceSnapshotSchema.nullable().optional(),
    preferredProcessor: ProcessorSchema.nullable().optional(),
    payments: z.array(PaymentSchema).nullable().optional(),
    shippingRate: z.object({
        id: z.string(),
        shippingRateName: z.string(),
        description: z.string().nullable().optional(),
        isFree: z.boolean().default(false),
        amount: z.record(z.string(), z.object({
            // Same JSONB drift as CurrencyOptionSnapshotSchema — coerce to survive legacy string writes.
            amount: z.coerce.number(),
            rate: z.coerce.number().optional(),
            lock: z.boolean().optional(),
            date: z.string().optional(),
        })),
        estimatedDeliveryTime: z.number().nullable().optional(),
    }).nullable().optional(),
});

export type Subscription = z.infer<typeof SubscriptionSchema>;
export type SubscriptionDetails = z.infer<typeof SubscriptionDetailsSchema>;

export const SubscriptionDto = (object: unknown): Subscription => SubscriptionSchema.parse(object);
export const SubscriptionDetailsDto = (object: unknown): SubscriptionDetails => SubscriptionDetailsSchema.parse(object);
